import {
    Box,
    Container,
    Flex,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    Menu,
    MenuButton,
    MenuDivider,
    MenuList,
    Spacer,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { AiOutlineInfoCircle, AiOutlineSend, AiOutlineWarning } from 'react-icons/ai';
import { BiSolidTimer } from 'react-icons/bi';
import { FaCookieBite } from 'react-icons/fa';
import { FiLogOut, FiMenu } from 'react-icons/fi';
import { forAnimationFrame } from 'waitasecond';
import { Contact } from './Contact';
import { EndGame } from './EndGame';
import { Message } from './Message';
import { Hints } from './hints/Hints';
import { useApi } from './hooks/Api';
import { useAppState } from './hooks/AppState';
import { Interval } from './utils/Interval';

interface IChatComponentProps {}

export const ChatComponent = observer((props: IChatComponentProps) => {
    const [message, setMessage] = useState<string>('');
    const appState = useAppState();
    const api = useApi();
    const toast = useToast();

    const submit = useCallback(async () => {
        if (message.length === 0) {
            return;
        }

        if (message.startsWith('$')) {
            toast({
                status: 'warning',
                title: 'Chyba',
                description: 'Nepovolená systémová zpráva, zprávy začínající znakem $ se neodešlou.',
            });

            return;
        }

        const response = await api.message(message, appState);

        if (response) {
            setMessage('');
        } else {
            toast({
                status: 'error',
                title: 'Chyba',
                description: 'Zprávu se nepodařilo odeslat.',
            });
        }
    }, [api, appState, message, toast]);

    if (!appState.appState) {
        return <></>;
    }

    return (
        <Box w="full" minH="100vh" bg="gray.700" color="gray.200">
            <HStack
                position="fixed"
                top={0}
                left={0}
                right={0}
                h={16}
                py={2}
                px={4}
                bg="gray.900"
                shadow="md"
                spacing={6}
                zIndex="sticky"
            >
                <Image src="logo.png" h="full" />
                <Spacer />
                <HStack spacing={4} color="gray.400" fontSize="sm">
                    <Text noOfLines={1}>{appState.appState!.teamName}</Text>
                </HStack>
                <IconButton
                    icon={<FiLogOut />}
                    aria-label="Odhlásit se"
                    onClick={() => {
                        appState.logout();
                        api.code = null;
                        localStorage.removeItem('code');
                    }}
                />
            </HStack>
            <Container py={8} maxW={{ base: 'full', md: 'container.sm', lg: 'container.md', xl: 'container.lg' }}>
                <VStack pt={14} spacing={2} alignItems="stretch">
                    <Flex direction="row">
                        <Input
                            flexGrow={1}
                            placeholder="Napište kód, nebo zprávu"
                            variant="outline"
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            onKeyDown={async (event) => {
                                if (event.key === 'Enter') {
                                    await forAnimationFrame();
                                    submit();
                                }
                            }}
                            autoFocus={true}
                        />
                        <IconButton
                            ml={2}
                            icon={<AiOutlineSend />}
                            variant="solid"
                            aria-label="Odeslat"
                            colorScheme="blue"
                            onClick={() => submit()}
                        />
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                ml={2}
                                colorScheme="gray"
                                variant="solid"
                                icon={<FiMenu />}
                                aria-label="Menu"
                            />
                            <MenuList>
                                <Hints />
                                <Contact />
                                <MenuDivider />
                                <EndGame
                                    onConfirm={async () => {
                                        const response = await api.message('$game_over', appState);

                                        if (!response) {
                                            toast({
                                                status: 'error',
                                                title: 'Chyba',
                                                description: 'Zprávu se nepodařilo odeslat.',
                                            });
                                        }
                                    }}
                                />
                            </MenuList>
                        </Menu>
                    </Flex>
                    <Text fontSize="small" fontStyle="italic" color="gray.500">
                        <Icon as={AiOutlineInfoCircle} mr={1} position="relative" top="2px" />
                        Zadejte název šifry, nebo kód. Pro nápovědy a kontakt na organizátory otevřete menu vpravo.
                    </Text>

                    <HStack mt={6} py={2} color="gray.300" key={appState.appState!.messages.length}>
                        <Text>
                            <strong>Aktuální šifra: </strong>
                            {appState.appState!.state.cipher}
                        </Text>
                        <Spacer />
                        {appState.appState!.state.arrivedAt && (
                            <Interval interval={1000}>
                                <HStack opacity={0.5}>
                                    <Icon as={BiSolidTimer} fontSize="larger" />
                                    <Text w={20}>
                                        {moment().diff(appState.appState!.state.arrivedAt, 'minutes')} min
                                    </Text>
                                </HStack>
                            </Interval>
                        )}
                        <HStack opacity={0.5}>
                            <Icon as={FaCookieBite} />
                            <Text w={10}>
                                {appState.appState!.state.gaveUp ? '∞' : `${appState.appState!.state.tokens}x`}
                            </Text>
                        </HStack>
                    </HStack>

                    {appState.appState!.state.gaveUp && (
                        <Text fontSize="small" color="yellow.500">
                            <Icon as={AiOutlineWarning} mr={1} position="relative" top="2px" />
                            Vzdali jste hru, můžete pokračovat dál s neomezeným počtem tokenů, váš výsledek se však bude počítat do momentu, kdy jste se vzdali.
                        </Text>
                    )}
                    <Flex direction="column" mt={8} gap={2} alignItems="stretch">
                        {appState
                            .appState!.messages.map((msg) => ({ ...msg, date: moment(msg.timestamp) }))
                            .sort((a, b) => b.id - a.id)
                            .map((msg, i, all) => (
                                <Message
                                    key={msg.id}
                                    {...msg}
                                    longTime={i > 0 && Math.abs(all[i - 1].date.diff(msg.date, 'minutes')) >= 1}
                                />
                            ))}
                    </Flex>
                </VStack>
            </Container>
        </Box>
    );
});
